import React from 'react'
import { Text } from '@chakra-ui/core'
import { ArrowUpRight } from 'lucide-react'
import { useSelector } from 'react-redux'
import { IPop } from 'constants/interfaces'
import { LinkPreview } from 'components/LinkPreview'
import { Button } from 'components/ui/Button'
import useLocaleText from 'components/useLocaleText'
import { isRtlSelector } from 'redux/selectors/general'
import { useLinkPopAction } from './hooks/useLinkPopAction'
import { MarkUpText } from './MarkUpText'
import { PopMedia } from './PopMedia'
import { LinkPopPreview } from './LinkPopPreview'
import { linkPopLiterals } from './model/literals'
import styles from './LinkPop.module.css'

interface ILinkPopContentProps {
  isOpen: boolean
  pop: IPop
  action: ReturnType<typeof useLinkPopAction>
}

export function LinkPopContent({ isOpen, pop, action }: ILinkPopContentProps) {
  const isRtl = useSelector(isRtlSelector)
  const openLink = useLocaleText(linkPopLiterals.openLink)
  const buttonText = useLocaleText(pop.ack_btn_text)
  const openFailed = useLocaleText(linkPopLiterals.openFailed)
  const acknowledgeFailed = useLocaleText(linkPopLiterals.acknowledgeFailed)
  const invalidUrl = useLocaleText(linkPopLiterals.urlInvalid)
  const { safeUrl, isPending, error, handleOpen } = action

  const errorMessage = !safeUrl
    ? invalidUrl
    : error === 'open'
    ? openFailed
    : error === 'ack'
    ? acknowledgeFailed
    : null

  const renderAction = (previewImage: React.ReactNode, previewTitle: string) => (
    <Button
      type="button"
      variant="primary"
      bg="#0F172A"
      color="white"
      width="100%"
      minHeight="64px"
      height="64px"
      px="0"
      py="0"
      lineHeight="20px"
      borderRadius="22px"
      borderWidth="0"
      fontSize="16px"
      fontWeight="600"
      textAlign={isRtl ? 'right' : 'left'}
      style={{ gap: '12px', paddingInlineStart: '10px', paddingInlineEnd: '20px' }}
      _hover={{ bg: '#1E293B', transform: 'none', boxShadow: 'none' }}
      _active={{ bg: '#020617', transform: 'none', boxShadow: 'none' }}
      isDisabled={!safeUrl || isPending}
      aria-busy={isPending}
      onClick={handleOpen}
    >
      {previewImage}
      <span className={styles.actionText}>
        <span className={styles.actionLabel}>
          {buttonText.trim() ? <MarkUpText text={buttonText} initiator={pop.pop_initiator} /> : openLink}
        </span>
        {previewTitle && (
          <span className={styles.actionSubtitle} dir="auto" title={previewTitle}>
            {previewTitle}
          </span>
        )}
      </span>
      <ArrowUpRight size={20} strokeWidth={2.75} className={styles.actionArrow} aria-hidden="true" />
    </Button>
  )

  return (
    <div className={styles.content} dir={isRtl ? 'rtl' : 'ltr'}>
      {typeof pop.content === 'string' && pop.content.trim() && (
        <Text
          textAlign={isRtl ? 'right' : 'left'}
          fontFamily="Arial"
          fontSize="20px"
          lineHeight="25px"
          whiteSpace="pre-wrap"
          style={{ overflowWrap: 'anywhere' }}
        >
          {pop.content}
        </Text>
      )}
      <PopMedia pop_image_url={pop.pop_image_url} video={pop.video} preserveImageAspectRatio />
      {safeUrl && isOpen ? (
        <LinkPreview
          key={safeUrl}
          url={safeUrl}
          renderPreview={(metadata, loading) => (
            <LinkPopPreview
              url={safeUrl}
              metadata={metadata}
              loading={loading}
              variant="action"
              renderAction={renderAction}
            />
          )}
        />
      ) : (
        <LinkPopPreview url={safeUrl} metadata={null} loading={false} variant="action" renderAction={renderAction} />
      )}
      {errorMessage && (
        <p className={styles.error} role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  )
}
